import { createTheme, CssBaseline, ThemeProvider } from '@mui/material';
import { enableMapSet } from 'immer';
import { Login, Register } from '@xolv-mybrightlink/security';
import {
  CareTeamSla,
  MessageContainer,
  CommunityPage,
  DimensionDetailPage,
  ProgressPortraitPage,
} from '@xolv-mybrightlink/mybrightlink';
import { StrictMode } from 'react';
import {
  BrowserRouter,
  Route,
  Routes as RouterRoutes,
  Outlet,
} from 'react-router-dom';
import './App.css';
import { AuthenticatedRoute } from './components/AuthenticatedRoute';
import { I18n } from './i18n/I18n';
import {
  LanguageProvider,
  RouteRender,
  RoutePaths,
  useAnalyticsSetup,
  EsNorcalProviderGuard,
} from '@xolv-mybrightlink/common';
import {
  ProfilePage,
  ProfileSetupBasic,
  ProfileSetupInterests,
  ProfileSetupBehaviors,
  ProfileSetupContext,
} from '@xolv-mybrightlink/mybrightlink';
import { DimensionClassPage } from '@xolv-mybrightlink/mybrightlink';
import { Home } from './components/Home';
import {
  ArticlePage,
  ArticleDetailPage,
  ArticleRoute,
} from '@xolv-mybrightlink/articles';
import {
  EventPage,
  EventDetailPage,
  EventRoute,
} from '@xolv-mybrightlink/events';
import * as Sentry from '@sentry/react';
import { ResourcesPage } from './pages/ResourcesPage';
import { AuthProviderRoute } from './components/AuthProviderRoute';
import { CoursePage, CourseDetailPage } from '@xolv-mybrightlink/mybrightlink';
import { CmsSavedPage } from './pages/CmsSavedPage';
import { RootEventHandler } from './RootEventHandler';
import { NotFoundPage } from './pages/NotFoundPage';
import { SurveyDetail } from '@xolv-mybrightlink/mybrightlink';

const theme = createTheme({
  spacing: 4,
  palette: {
    primary: {
      main: '#307FE2',
    },
    secondary: {
      main: '#f6cce2',
      contrastText: '#d00070',
    },
    info: {
      main: '#ffffff',
      contrastText: '#3b4559',
    },
    text: {
      primary: '#3b4559',
      disabled: '#898f9b',
    },
    error: {
      main: '#D9338D',
      light: '#F6CCE2',
    },
    action: {
      active: '#307FE2',
    },
  },
  typography: {
    fontFamily: 'Poppins',
    subtitle1: {
      fontSize: 20,
    },
    subtitle2: {
      fontSize: 12,
    },
    button: {
      textTransform: 'none',
    },
  },
});

const SentryEnhancedRoutes = __JS_ENV_VARS__['sentry-dsn']
  ? Sentry.withSentryReactRouterV6Routing(RouterRoutes)
  : RouterRoutes;

enableMapSet();

export const App = () => {
  useAnalyticsSetup('myBrightlink_web');

  return (
    <StrictMode>
      <Sentry.ErrorBoundary
        fallback={
          <p>
            An error has occurred, please refresh the page to see if the problem
            persists.
          </p>
        }
      >
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <LanguageProvider>
            <I18n>
              <BrowserRouter>
                <SentryEnhancedRoutes>
                  <Route
                    path={`/${RoutePaths.home}`}
                    element={
                      <RootEventHandler>
                        <AuthProviderRoute />
                      </RootEventHandler>
                    }
                  >
                    <Route path='' element={<AuthenticatedRoute />}>
                      <Route index element={<Home />} />
                      <Route
                        path={RoutePaths.survey}
                        element={<SurveyDetail />}
                      />
                      <Route path={RoutePaths.profile} element={<Outlet />}>
                        <Route index element={<ProfilePage />} />
                        <Route element={<Outlet />}>
                          <Route element={<ProfileSetupContext />}>
                            <Route
                              path={RoutePaths.basic}
                              element={<ProfileSetupBasic />}
                            />
                            <Route
                              path={RoutePaths.interests}
                              element={<ProfileSetupInterests />}
                            />
                            <Route
                              path={RoutePaths.behaviors}
                              element={<ProfileSetupBehaviors />}
                            />
                          </Route>
                        </Route>
                      </Route>
                      <Route path={RoutePaths.messages} element={<Outlet />}>
                        <Route
                          index
                          element={<MessageContainer viewMode='none' />}
                        />
                        <Route
                          path={RoutePaths.info}
                          element={<MessageContainer viewMode='info' />}
                        />
                        <Route
                          path={`${RoutePaths.careTeam}/${RoutePaths.sla}`}
                          element={<CareTeamSla />}
                        />
                        <Route
                          path=':channelGuid'
                          element={<MessageContainer viewMode='conversation' />}
                        />
                        <Route
                          path={`:channelGuid/${RoutePaths.gallery}`}
                          element={<MessageContainer viewMode='gallery' />}
                        />
                        <Route
                          path={`:channelGuid/${RoutePaths.members}`}
                          element={<MessageContainer viewMode='members' />}
                        />
                        <Route
                          path={RoutePaths.community}
                          element={<MessageContainer viewMode='community' />}
                        />
                        <Route
                          path={`${RoutePaths.community}/${RoutePaths.sla}`}
                          element={
                            <MessageContainer viewMode='community-sla' />
                          }
                        />
                      </Route>
                      <Route
                        path={RoutePaths.saved}
                        element={<CmsSavedPage />}
                      />
                      <Route
                        path={RoutePaths.community}
                        element={<CommunityPage />}
                      />
                      <Route
                        path={RoutePaths.progressPortrait}
                        element={
                          <EsNorcalProviderGuard>
                            <ProgressPortraitPage />
                          </EsNorcalProviderGuard>
                        }
                      />
                      <Route path={RoutePaths.resources} element={<Outlet />}>
                        <Route index element={<ResourcesPage />} />
                        <Route path={RoutePaths.courses} element={<Outlet />}>
                          <Route index element={<CoursePage />} />
                          <Route
                            path=':courseId'
                            element={<CourseDetailPage />}
                          />
                          <Route
                            path=':courseId/:lessonId'
                            element={<CourseDetailPage />}
                          />
                          <Route
                            path=':courseId/:lessonId/:quizId'
                            element={<CourseDetailPage />}
                          />
                        </Route>
                        <Route
                          path={RoutePaths.articles}
                          element={
                            <ArticleRoute>
                              <Outlet />
                            </ArticleRoute>
                          }
                        >
                          <Route
                            index
                            element={<ArticlePage key={'all-articles'} />}
                          />
                          <Route
                            path={':articleSlug'}
                            element={<ArticleDetailPage />}
                          />
                          <Route
                            path={`${RoutePaths.category}/:categoryId/:articleSlug`}
                            element={<ArticleDetailPage />}
                          />
                          <Route
                            path={`${RoutePaths.category}/:categoryId`}
                            element={
                              <RouteRender
                                render={(params) => (
                                  <ArticlePage key={params.categoryId} />
                                )}
                              />
                            }
                          />
                        </Route>
                        <Route path={RoutePaths.dimensions}>
                          <Route index element={<DimensionClassPage />} />
                          <Route
                            path=':postId'
                            element={<DimensionDetailPage />}
                          />
                        </Route>
                        <Route
                          path={RoutePaths.events}
                          element={
                            <EventRoute>
                              <Outlet />
                            </EventRoute>
                          }
                        >
                          <Route
                            index
                            element={<EventPage key='all-events' />}
                          />
                          <Route
                            path={`${RoutePaths.category}/:categoryId/:eventDatabaseId`}
                            element={<EventDetailPage />}
                          />
                          <Route
                            path={`${RoutePaths.category}/:categoryId`}
                            element={
                              <RouteRender
                                render={(params) => (
                                  <EventPage key={params.categoryId} />
                                )}
                              />
                            }
                          />
                        </Route>
                      </Route>
                    </Route>
                    <Route path={RoutePaths.login} element={<Login />} />
                    <Route path={RoutePaths.register} element={<Register />} />
                  </Route>
                  <Route path='*' element={<NotFoundPage />} />
                </SentryEnhancedRoutes>
              </BrowserRouter>
            </I18n>
          </LanguageProvider>
        </ThemeProvider>
      </Sentry.ErrorBoundary>
    </StrictMode>
  );
};
